import React, { useEffect, useState } from 'react';
import { set, cloneDeep } from 'lodash';
import { Grid, IconButton, InputAdornment } from '@material-ui/core';
import { Email } from '@material-ui/icons';
import {
  LoadingBounce, PhoneInput, TextField,
} from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { getOptions } from '@michelin/select-options-provider';
import { hasEmailFormat } from 'Utils';
import {
  SPPContactTypeLevelsEnum, SPPContactTypeServicesEnum, SPPContactTypeRoleAreasEnum, POSDetails,
} from 'Types';
import ListingFilterSelect from '@michelin/acid-components/build/components/ListingFilters/ListingFilterSelect';
import { AssignContact } from 'components/Contact/AssignContact';

interface POSProps {
  action: string,
  shipToNumber: string,
  billToNumber: string,
  countryCode: string,
  posDetails: POSDetails,
  validationErrors: any,
  onChange: (posDetails: POSDetails, validationErrors: any) => void
}

interface POSValidationErrors {
  pos_provider: boolean,
  pos_provider_technical_contact_email: boolean,
  pos_provider_technical_contact_phone: boolean,
  other_name: boolean
}
interface GlobalTextInputProps {
  variant?: 'standard',
  InputProps: any,
  style: any,
  onChange?: (event: React.ChangeEvent<any> | null, value?: any) => void
}

function POS(props: POSProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [optionList, setOptionList] = useState(null);

  const validationErrorsClone: POSValidationErrors = cloneDeep(props.validationErrors);
  const list: any | null = getOptions('pos_providers');
  // Sorting optionsList to force "Other" to the end
  list.sort((a: any, b: any) => {
    if (a.label === 'Other') return 1;
    if (b.label === 'Other') return -1;
    return a.label > b.label ? 1 : -1;
  });

  useEffect(() => {
    setLoading(true);
    setOptionList(list);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateData = (target: any) => {
    switch (target.id) {
      case 'pos_provider_technical_contact_phone':
        validationErrorsClone.pos_provider_technical_contact_phone = !target.value || !target.isPossible;
        break;
      case 'other_name':
        validationErrorsClone.other_name = !target.value && props.posDetails.pos_provider === 'OTHER';
        break;
      case 'pos_provider':
        validationErrorsClone.pos_provider = !target.value;
        validationErrorsClone.other_name = target.value === 'OTHER' && !props.posDetails.other_name;
        break;
      case 'pos_provider_technical_contact_email':
        validationErrorsClone.pos_provider_technical_contact_email = (target.value.length !== 0 && !hasEmailFormat(target.value));
        break;
      default:
        break;
    }
  };

  const updateValueFromTarget = (dispatch: any) => {
    if (!props.posDetails) return;
    const posDetailsClone = cloneDeep(props.posDetails);

    validateData(dispatch.target);
    set(posDetailsClone, dispatch.target.id, dispatch.target.value);

    props.onChange(posDetailsClone, validationErrorsClone);
  };

  const updateValue = (event: React.ChangeEvent<any> | null, newValue: any): void => {
    if (!props.posDetails) return;
    if (!event) return;

    const posDetailsClone = cloneDeep(props.posDetails);

    validateData(event.target);
    set(posDetailsClone, event.target.id, event.target.value || newValue);

    props.onChange(posDetailsClone, validationErrorsClone);
  };

  function updatePhoneValue(target: any) {
    if (!props.posDetails) return;
    validateData(target);
    const posDetailsClone = cloneDeep(props.posDetails);
    set(posDetailsClone, target.id, target.value);
    props.onChange(posDetailsClone, validationErrorsClone);
  }

  const renderPOSDetails = (optionsList: any): JSX.Element => {
    const readOnly: boolean = props.action === 'view';
    const defaultText: string = '';
    const textInputProps: GlobalTextInputProps = {
      variant: 'standard',
      InputProps: { readOnly },
      style: { display: 'flex', flexWrap: 'wrap' },
      onChange: !readOnly ? updateValue : undefined,
    };

    if (!optionsList) return <LoadingBounce style={{ height: '70vh' }} />;

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ListingFilterSelect
              id="pos_provider"
              value={props.posDetails.pos_provider || defaultText}
              variant="standard"
              title={t('POS Provider')}
              required
              options={optionsList}
              readOnly={readOnly}
              onChange={updateValueFromTarget}
              error={!props.posDetails.pos_provider}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {(props.posDetails.pos_provider === 'OTHER')
              && (
                <TextField
                  id="other_name"
                  label={t('Name of POS Provider')}
                  value={props.posDetails.other_name || defaultText}
                  error={!props.posDetails.other_name}
                  variant={textInputProps.variant}
                  style={textInputProps.style}
                  onChange={textInputProps.onChange}
                  readOnly={readOnly}
                  required
                />
              )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              id="customer_number_with_pos_provider"
              label={t("Dealer's Account Number with the POS Provider")}
              value={props.posDetails.customer_number_with_pos_provider || defaultText}
              variant={textInputProps.variant}
              style={textInputProps.style}
              onChange={textInputProps.onChange}
              readOnly={readOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              id="pos_provider_technical_contact_name"
              label={t("POS Provider Technical Contact's Name")}
              value={props.posDetails.pos_provider_technical_contact_name || defaultText}
              variant={textInputProps.variant}
              style={textInputProps.style}
              onChange={textInputProps.onChange}
              readOnly={readOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              error={validationErrorsClone.pos_provider_technical_contact_email}
              id="pos_provider_technical_contact_email"
              label={t("POS Provider Technical Contact's Email Address")}
              value={props.posDetails.pos_provider_technical_contact_email || defaultText}
              variant={textInputProps.variant}
              style={textInputProps.style}
              onChange={textInputProps.onChange}
              readOnly={readOnly}
              InputProps={{
                readOnly,
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      aria-label={t('POS Provider Technical Contact\'s Email Address')}
                      disabled
                    >
                      <Email />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneInput
              id="pos_provider_technical_contact_phone"
              label={t("POS Provider Technical Contact's Phone Number")}
              value={props.posDetails.pos_provider_technical_contact_phone || defaultText}
              defaultCountry={(props.countryCode.toLowerCase() === 'ca' ? 'CA' : 'US')}
              readOnly={readOnly}
              required={false}
              onChange={!readOnly ? (event: any) => { updatePhoneValue(event); } : undefined}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <AssignContact
              shipToNumber={props.shipToNumber}
              billToNumber={props.billToNumber}
              countryCode={props.countryCode}
              title={t('POS Contact')}
              service={SPPContactTypeServicesEnum.retail}
              roleArea={SPPContactTypeRoleAreasEnum.pos_administrator}
              priorityLevels={[SPPContactTypeLevelsEnum.primary, SPPContactTypeLevelsEnum.secondary]}
              mode={props.action}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  if (loading) return <LoadingBounce style={{ height: '70vh' }} />;

  return renderPOSDetails(optionList);
}

export default POS;
